import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Button, InputLabel, Table, TextInput, ValueListInput } from '@components/Common';
import DateTimePicker from '@components/Common/DateTimePicker/DateTimePicker';
import { ValueType } from '@components/Common/ValueListInput';
import { formatLocalDate } from '@helpers/formatting';
import { TransportationCompanyVehicle } from '@api/queries/transportationCompanies';

const InputRow = styled.div`
  display: flex;
  flex-direction: row;

  > *:nth-child(1) {
    margin-right: 10px;
  }

  > * {
    width: 50%;
  }
`;

const ButtonRow = styled.div`
  margin-top: 15px;
  > * {
    margin-right: 10px;
  }
`;

const EditorContainer = styled.div`
  border: 1px solid #ccc;
  padding: 10px;
  margin-top: 3px;
  margin-bottom: 30px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;

  > *:nth-child(2) {
    padding: 10px 5px 0px;
    margin: 10px auto;
    text-align: right;
  }

  > * {
    width: 50%;
  }
`;

const defaultVehicle: TransportationCompanyVehicle = {
  id: null,
  adrCertificateValidUntil: null,
  compartments: [],
  isDeleted: false,
  notes: '',
  register: ''
};

interface Props {
  values?: TransportationCompanyVehicle[];
  onChange: (newValues: TransportationCompanyVehicle[]) => void;
  editorItem: TransportationCompanyVehicle | null;
  onSetEditorItem: (item: TransportationCompanyVehicle | null) => void;
}

const Vehicles: React.FC<Props> = ({ values = [], editorItem, onChange, onSetEditorItem }) => {
  const { t } = useTranslation();
  const columns = [
    {
      Header: t('transportationCompanies.register'),
      id: 'register',
      accessor: 'register'
    },
    {
      Header: t('transportationCompanies.adr_valid_until'),
      id: 'adrCertificateValidUntil',
      accessor: 'adrCertificateValidUntil',
      Cell: ({ value }) => formatLocalDate(value),
      maxWidth: 150
    },
    {
      Header: t('transportationCompanies.compartments'),
      id: 'compartments',
      accessor: 'compartments',
      Cell: ({ value }) => value.join(', '),
      maxWidth: 150
    },
    {
      Header: t('transportationCompanies.notes'),
      id: 'notes',
      accessor: 'notes'
    }
  ];

  const handleEditorSave = (item: TransportationCompanyVehicle) => {
    if (!item.id) {
      onChange([...values, { ...item, id: uuidv4() }]);
    } else {
      const index = values.findIndex(v => v.id === item.id);
      const newValues = [...values];
      newValues[index] = item;
      onChange(newValues);
    }

    onSetEditorItem(null);
  };

  const handleEditorRemove = (item: TransportationCompanyVehicle) => {
    if (confirm(t('transportationCompanies.confirm_delete_vehicle'))) {
      const newValues = values.filter(v => v !== item);
      onSetEditorItem(null);
      onChange(newValues);
    }
  };

  const handleEditorCancel = () => {
    onSetEditorItem(null);
  };

  const activeValues = values.filter(v => !v.isDeleted);

  return (
    <div>
      <TitleRow>
        <InputLabel>{t('transportationCompanies.vehicles')}</InputLabel>
        <div>
          <a onClick={e => onSetEditorItem({ ...defaultVehicle })}>{t('transportationCompanies.add_vehicle')}</a>
        </div>
      </TitleRow>
      {activeValues.length > 0 && (
        <Table
          data={activeValues}
          columns={columns}
          filterable={false}
          showPagination={false}
          defaultSortColumn="register"
          defaultSortDesc={true}
          key={activeValues.length}
          defaultPageSize={activeValues.length}
          onRowSelected={({ original }) => onSetEditorItem(original)}
          rowSelector={i => editorItem && editorItem.id === i.original.id}
        />
      )}
      {!!editorItem && (
        <VehicleEditor
          key={editorItem.id || 'new'}
          item={editorItem}
          onSave={handleEditorSave}
          onRemove={handleEditorRemove}
          onCancel={handleEditorCancel}
        />
      )}
    </div>
  );
};

interface VehicleEditorProps {
  item: TransportationCompanyVehicle;
  onSave: (item: TransportationCompanyVehicle) => void;
  onRemove: (item: TransportationCompanyVehicle) => void;
  onCancel: () => void;
}

const VehicleEditor: React.FC<VehicleEditorProps> = ({ item, onSave, onRemove, onCancel }) => {
  const { t } = useTranslation();
  const [editItem, setEditItem] = useState({ ...item });
  const { register, adrCertificateValidUntil, compartments, notes } = editItem;

  const handleTextInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditItem({
      ...editItem,
      [e.target.name]: e.target.value
    });
  };

  const handleAdrChanged = (newValue: string) => {
    setEditItem({
      ...editItem,
      adrCertificateValidUntil: newValue || null
    });
  };

  const handleCompartmentsChanged = (newValues: any[]) => {
    setEditItem({
      ...editItem,
      compartments: newValues
    });
  };

  const isNew = !editItem.id;
  const isValid = register.trim().length > 0 && compartments.length > 0;

  const handleSetClick = () => {
    if (isValid) {
      onSave(editItem);
    }
  };

  const handleRemove = () => {
    onRemove(item);
  };

  return (
    <EditorContainer>
      <InputRow>
        <div>
          <InputLabel htmlFor="register">{t('transportationCompanies.register')} *</InputLabel>
          <TextInput
            id="register"
            name="register"
            type="text"
            value={register}
            onChange={handleTextInputChanged}
            autoFocus
          />
        </div>
        <div>
          <InputLabel htmlFor="compartments">
            {t('transportationCompanies.compartments')} m<sup>3</sup> ({t('transportationCompanies.compartments_info')})
            *
          </InputLabel>
          <ValueListInput
            valueType={ValueType.Integer}
            name="compartments"
            placeholder={t('transportationCompanies.compartments_placeholder')}
            values={compartments}
            onChange={handleCompartmentsChanged}
          />
        </div>
      </InputRow>
      <InputRow>
        <div>
          <DateTimePicker
            title={t('transportationCompanies.adr_valid_until_full')}
            name="adrCertificateValidUntil"
            dateTime={adrCertificateValidUntil}
            hasTime={false}
            showTime={false}
            changed={handleAdrChanged}
          />
        </div>
        <div>
          <InputLabel htmlFor="notes">{t('transportationCompanies.notes')}</InputLabel>
          <TextInput id="notes" name="notes" value={notes} onChange={handleTextInputChanged} />
        </div>
      </InputRow>
      <ButtonRow>
        <Button type="button" secondary={true} onClick={onCancel} small={true}>
          {t('cancel')}
        </Button>
        {!isNew && (
          <Button type="button" danger={true} onClick={handleRemove} small={true}>
            {t('delete')}
          </Button>
        )}
        <Button primary={true} disabled={!isValid} onClick={handleSetClick} small={true}>
          {t('set')}
        </Button>
      </ButtonRow>
    </EditorContainer>
  );
};

export default Vehicles;
